import React, { PropsWithChildren, useEffect, useReducer } from 'react';
import * as R from 'ramda';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import {
  useAppBootstrappingAppName,
  useAppBootstrappingBrand,
} from '@dxb-web-ui-framework/common/contexts/AppBootstrapping/hooks';
import { useTranslation } from 'react-i18next';
import { Locale } from '../../domain/Internationalization/entities';
import { InternationalizationProviderProps, InternationalizationContextActionTypes } from './types';
import { InternationalizationContext, InternationalizationDispatchContext } from './InternationalizationContext';
import { InternationalizationReducer } from './InternationalizationReducer';
import { DEFAULT_LANGUAGE, defaultInternationalizationState } from './defaultState';

const InternationalizationProvider = ({
  internationalizationService,
  loaderComponent,
  errorComponent,
  children,
}: PropsWithChildren<InternationalizationProviderProps>): JSX.Element => {
  const brand = useAppBootstrappingBrand();
  const appName = useAppBootstrappingAppName();
  const { i18n } = useTranslation();
  const channelRelativePath = `${appName}/${brand}/${appName}`;

  const [state, dispatch] = useReducer(InternationalizationReducer, defaultInternationalizationState);

  const {
    language: { cultureName },
  } = state;

  const {
    data: availableLanguages,
    isLoading: availableLanguagesIsLoading,
    error: availableLanguagesError,
  } = useInvokeSWRService(
    internationalizationService.GetAvailableLanguages,
    {
      channelRelativePath,
    },
    Boolean(channelRelativePath),
    'internationalizationService.GetAvailableLanguages',
  );

  const {
    data: translations,
    isLoading: translationsIsLoading,
    error: translationsError,
  } = useInvokeSWRService(
    internationalizationService.GetTranslations,
    {
      channelRelativePath,
      locale: state.language.cultureName,
    },
    Boolean(channelRelativePath && state.language.cultureName),
    'internationalizationService.GetTranslations',
  );

  // TODO: loader and error on persist language
  const { data: languagePersisted } = useInvokeSWRService(
    internationalizationService.PersistLanguage,
    { language: state.language },
    // TODO: Use value state.languageChanged instead of false, once the endpoint has been provided by middleware team
    false,
    'internationalizationService.PersistLanguage',
  );

  useEffect(() => {
    if (availableLanguages) {
      const lang = R.find(locale => locale.cultureName === state.language.cultureName, availableLanguages as Locale[]);

      dispatch({
        type: InternationalizationContextActionTypes.SetAvailableLanguages,
        payload: { availableLanguages, language: lang || DEFAULT_LANGUAGE },
      });
    }
  }, [availableLanguages, state.language]);

  useEffect(() => {
    if (translations) {
      // TODO probably not needed as translations stored in react-i18n
      dispatch({ type: InternationalizationContextActionTypes.SetTranslations, payload: translations });
      // TODO revise proper schema after fetching translations
      const resources = translations.reduce((acc: GenericObject, { value, localValue }) => {
        acc[value] = localValue;
        return acc;
      }, {});
      i18n.addResources(cultureName, 'translation', resources);
      i18n.changeLanguage(cultureName);
    }
  }, [availableLanguages, translations, cultureName, i18n]);

  useEffect(() => {
    if (languagePersisted) {
      // // TODO: use navigateTo method and the routePath helper to navigate to dashboard
      // window.location.href = '/';
    }
  }, [languagePersisted]);

  const error = availableLanguagesError || translationsError;
  const isLoading = availableLanguagesIsLoading || translationsIsLoading;

  return (
    <InternationalizationContext.Provider value={state}>
      <InternationalizationDispatchContext.Provider value={dispatch}>
        {error && errorComponent}
        {isLoading && loaderComponent}
        {translations && availableLanguages && children}
      </InternationalizationDispatchContext.Provider>
    </InternationalizationContext.Provider>
  );
};

export { InternationalizationProvider };
